import { Euler, Vector2, Vector3 } from 'three';


export type Position2D = {
    x: number;
    y: number;
};

export type Position = Position2D & {
    z: number;
};

type NumArr2 = [number, number];
type NumArr3 = [number, number, number];

export type Vector2Representation = number | THREE.Vector2 | NumArr2 | Position2D;
export type Vector3Representation = number | THREE.Vector3 | NumArr3 | Position;
export type EulerRepresentation = number | THREE.Euler | NumArr3 | Position;

export namespace Position {
    export function fromArray(c: [number, number, number], compute: (v: number | string) => number = (v => v as any)) {
        return {
            x: compute(c[0]),
            y: compute(c[1]),
            z: compute(c[2]),
        };
    }
    export function extract(c: Vector3Representation | EulerRepresentation, compute: (v: number | string) => number = (v => v as any)): Position {
        if (c == null) {
            return null;
        }

        if (Array.isArray(c)) {
            return fromArray(c, compute);
        }

        if (typeof c === 'number') {
            const v = compute(c);
            return { x: v, y: v, z: v };
        }

        return {
            x: compute(c.x),
            y: compute(c.y),
            z: compute(c.z),
        };
    }

    export function parse2d(value: Vector2Representation, target?: Vector2): Vector2 {
        if (value == null) {
            return target;
        }

        if (value instanceof Vector2) {
            if (target) {
                target.copy(value);
                return target;
            }
            return value;
        }

        if (typeof value === 'number') {
            const c = target ?? new Vector2();
            c.set(value, value);
            return c;
        }

        if (Array.isArray(value)) {
            const c = target ?? new Vector2();
            c.set(value[0], value[1]);
            return c;
        }

        if (typeof value.x !== 'number' || typeof value.y !== 'number') {
            return target;
        }

        if (target) {
            target.set(value.x, value.y);
            return target;
        }

        return new Vector2(value.x, value.y);
    }

    export function parse3d(value: Vector3Representation, target?: Vector3): Vector3 {
        if (value == null) {
            return target;
        }

        if (value instanceof Vector3) {
            if (target) {
                target.copy(value);
                return target;
            }
            return value;
        }

        if (typeof value === 'number') {
            const c = target ?? new Vector3();
            c.set(value, value, value);
            return c;
        }

        if (Array.isArray(value)) {
            const c = target ?? new Vector3();
            c.set(value[0], value[1], value[2]);
            return c;
        }

        if (typeof value.x !== 'number' || typeof value.y !== 'number' || typeof value.z !== 'number') {
            return target;
        }

        if (target) {
            target.set(value.x, value.y, value.z);
            return target;
        }

        return new Vector3(value.x, value.y, value.z);
    }

    export function parseEuler(value: EulerRepresentation, target?: Euler): Euler {
        if (value == null) {
            return target;
        }

        if (value instanceof Euler) {
            if (target) {
                target.copy(value);
                return target;
            }
            return value;
        }

        if (typeof value === 'number') {
            const c = target ?? new Euler();
            c.set(value, value, value);
            return c;
        }

        if (Array.isArray(value)) {
            const c = target ?? new Euler();
            c.set(value[0], value[1], value[2]);
            return c;
        }

        if (typeof value.x !== 'number' || typeof value.y !== 'number' || typeof value.z !== 'number') {
            return target;
        }

        if (target) {
            target.set(value.x, value.y, value.z);
            return target;
        }

        return new Euler(value.x, value.y, value.z);
    }

    export function clone2d(value: Vector2): Position2D { return ({ x: value.x, y: value.y }); }
    export function clone3d(value: Vector3): Position { return ({ x: value.x, y: value.y, z: value.z }); }
    export function cloneEuler(value: Euler): Position { return ({ x: value.x, y: value.y, z: value.z }); }
}
